import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';

const ContentTransferAnimation = ({ 
  isAnimating, 
  onAnimationComplete,
  platforms = ['linkedin', 'twitter'],
  duration = 3000 
}) => {
  const [animationState, setAnimationState] = useState('idle'); // idle, transferring, complete
  const [currentPlatform, setCurrentPlatform] = useState(0);

  useEffect(() => {
    if (isAnimating) {
      setAnimationState('transferring');
      setCurrentPlatform(0);
      
      // Animation sequence
      const timer = setTimeout(() => {
        setAnimationState('complete');
        setTimeout(() => {
          onAnimationComplete?.();
        }, 500);
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setAnimationState('idle');
    }
  }, [isAnimating, duration, onAnimationComplete]);

  if (!isAnimating && animationState === 'idle') {
    return null;
  }

  const platformIcons = {
    linkedin: 'Linkedin',
    twitter: 'X'
  };

  const platformColors = {
    linkedin: '#0A66C2',
    twitter: '#000000'
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-card rounded-2xl p-8 max-w-md w-full mx-4 border border-border shadow-2xl">
        <div className="text-center mb-6">
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Publishing Content
          </h3>
          <p className="text-sm text-muted-foreground">
            Sending your content to social platforms...
          </p>
        </div>

        {/* Animation Container */}
        <div className="relative h-32 flex items-center justify-between px-4">
          {/* Source Platform (Our App) */}
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-2 shadow-lg">
              <Icon name="FileText" size={24} color="white" />
            </div>
            <span className="text-xs text-muted-foreground font-medium">Our Platform</span>
          </div>

          {/* Animated Content Transfer */}
          <div className="flex-1 relative mx-6">
            {/* Transfer Line */}
            <div className="absolute top-1/2 left-0 right-0 h-1 bg-border rounded-full transform -translate-y-1/2">
              <div 
                className={`h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all duration-1000 ease-in-out ${
                  animationState === 'transferring' ? 'w-full' : 'w-0'
                }`}
              />
            </div>


            {/* Direction Arrow */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <Icon 
                name="ArrowRight" 
                size={20} 
                color={animationState === 'transferring' ? 'var(--color-primary)' : 'var(--color-muted-foreground)'} 
                className="transition-all duration-300"
              />
            </div>
          </div>

          {/* Destination Platforms */}
          <div className="flex flex-col items-center space-y-4">
            {platforms.map((platform, index) => (
              <div key={platform} className="flex flex-col items-center">
                <div 
                  className={`w-12 h-12 rounded-full flex items-center justify-center shadow-md transition-all duration-500 ${
                    animationState === 'transferring' || animationState === 'complete'
                      ? 'scale-110 ring-2 ring-primary ring-opacity-50' 
                      : 'scale-100'
                  }`}
                  style={{ 
                    backgroundColor: platformColors[platform],
                    animationDelay: `${index * 200}ms`
                  }}
                >
                  <Icon 
                    name={platformIcons[platform]} 
                    size={16} 
                    color="white" 
                  />
                </div>
                <span className="text-xs text-muted-foreground font-medium mt-1">
                  {platform === 'linkedin' ? 'LinkedIn' : 'X'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Status Text */}
        <div className="text-center mt-6">
          <p className="text-sm text-muted-foreground">
            {animationState === 'transferring' && 'Transferring content...'}
            {animationState === 'complete' && (
              <span className="text-green-600 dark:text-green-400 flex items-center justify-center gap-2">
                <Icon name="CheckCircle" size={16} />
                Content published successfully!
              </span>
            )}
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mt-4">
          <div className="w-full bg-border rounded-full h-1">
            <div
              className={`h-1 rounded-full transition-all duration-1000 ease-out ${
                animationState === 'transferring' ? 'w-full' : animationState === 'complete' ? 'w-full' : 'w-0'
              }`}
              style={{ background: 'linear-gradient(90deg, #27272A, #52525B)' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentTransferAnimation;